/**
 * Customer Portal — public, no-auth landing page.
 *
 * Each customer record carries a `portal_token` (UUID). The admin shares
 * the link `/portal/c/{token}` and the customer can see their full history
 * (invoices + quotes) plus a Pay Now button on every unpaid invoice.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2,
  AlertCircle,
  Receipt,
  CheckCircle,
  Clock,
  FileText,
  ExternalLink,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (a) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

const STATUS_BADGE = {
  paid: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-sky-100 text-sky-700',
  draft: 'bg-slate-100 text-slate-600',
  overdue: 'bg-rose-100 text-rose-700',
  void: 'bg-slate-100 text-slate-400 line-through',
  approved: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
  converted: 'bg-violet-100 text-violet-700',
};

export default function CustomerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/portal/customer/${token}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? 'This portal link is no longer valid.'
              : 'Could not load your account.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <FullPage>
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </FullPage>
    );
  }
  if (error) {
    return (
      <FullPage>
        <AlertCircle className="h-12 w-12 text-rose-500 mb-3" />
        <p className="text-slate-700">{error}</p>
      </FullPage>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900 py-10 px-4"
      data-testid="customer-portal-page"
    >
      <div className="max-w-4xl mx-auto">
        {/* Branding */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border flex items-center justify-center">
            <img src="/brand/logo.png" alt="My Key Boss" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="font-bold">{data.company?.name || 'My Key Boss'}</p>
            <p className="text-xs text-slate-500">{data.company?.email}</p>
          </div>
        </div>

        {/* Greeting */}
        <div className="bg-white rounded-2xl border shadow-sm p-6 mb-6">
          <p className="text-sm text-slate-500 mb-1">Customer Portal</p>
          <h1 className="text-2xl font-bold" data-testid="portal-customer-name">
            Welcome, {data.customer?.name}
          </h1>
          {data.customer?.email && (
            <p className="text-sm text-slate-500 mt-1">{data.customer.email}</p>
          )}
        </div>

        {/* Totals */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Stat label="Outstanding" value={fmt(data.totals?.total_outstanding)} accent="text-amber-600" testid="portal-stat-outstanding" />
          <Stat label="Paid to Date" value={fmt(data.totals?.total_paid)} accent="text-emerald-600" testid="portal-stat-paid" />
          <Stat label="Open Invoices" value={data.totals?.open_invoices ?? 0} accent="text-sky-600" testid="portal-stat-open" />
        </div>

        {/* Invoices */}
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold">Your Invoices</h2>
            <span className="text-xs text-slate-500 ml-auto">{data.invoices?.length || 0} total</span>
          </div>
          {data.invoices?.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No invoices yet.</div>
          ) : (
            <ul className="divide-y" data-testid="portal-invoices-list">
              {data.invoices.map((inv) => {
                const badge = STATUS_BADGE[inv.status] || 'bg-slate-100 text-slate-600';
                const isPayable = ['draft', 'sent', 'overdue'].includes(inv.status);
                return (
                  <li
                    key={inv.invoice_number}
                    className="px-5 py-4 flex items-center gap-3 flex-wrap"
                    data-testid={`portal-invoice-${inv.invoice_number}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{inv.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${badge} font-medium`}>
                          {inv.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {inv.invoice_number}
                        {inv.due_at && inv.status !== 'paid' ? ` · Due ${inv.due_at.slice(0, 10)}` : ''}
                        {inv.paid_at ? ` · Paid ${inv.paid_at.slice(0, 10)}` : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">{fmt(inv.total)}</p>
                    </div>
                    {isPayable && inv.public_token && (
                      <a
                        href={`/pay/i/${inv.public_token}`}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600"
                        data-testid={`portal-pay-${inv.invoice_number}`}
                      >
                        Pay Now <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {inv.status === 'paid' && (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold">
                        <CheckCircle className="h-4 w-4" /> Paid
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Quotes */}
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <FileText className="h-5 w-5 text-violet-600" />
            <h2 className="font-semibold">Your Quotes</h2>
            <span className="text-xs text-slate-500 ml-auto">{data.quotes?.length || 0} total</span>
          </div>
          {data.quotes?.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No quotes yet.</div>
          ) : (
            <ul className="divide-y" data-testid="portal-quotes-list">
              {data.quotes.map((q) => {
                const badge = STATUS_BADGE[q.status] || 'bg-slate-100 text-slate-600';
                return (
                  <li key={q.quote_number} className="px-5 py-4 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{q.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${badge} font-medium`}>
                          {q.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {q.quote_number}
                        {q.valid_until ? ` · Valid until ${String(q.valid_until).slice(0, 10)}` : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">{fmt(q.total)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-2 flex items-center justify-center gap-1">
          <Clock className="h-3 w-3" /> Powered by My Key Boss
        </p>
      </div>
    </div>
  );
}

const FullPage = ({ children }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-900 p-6 text-center">
    {children}
  </div>
);

const Stat = ({ label, value, accent, testid }) => (
  <div className="bg-white rounded-xl border shadow-sm p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</p>
    <p className={`text-2xl font-bold ${accent}`}>{value}</p>
  </div>
);
